import type { ReactNode } from "react";
import { movieGenres, regions, tvGenres, works } from "@/mocks/works";
import type { WorkType } from "@/types/media";

export interface CategoryFilterState {
  regions: string[];
  years: number[];
  genres: string[];
  minRating: number;
  sort: "hot" | "year" | "rating";
}

interface FilterPanelProps {
  type: WorkType;
  value: CategoryFilterState;
  onChange: (next: CategoryFilterState) => void;
}

function Chip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs md:text-sm transition-colors whitespace-nowrap cursor-pointer ${
        active
          ? "bg-primary-500 text-background-50 font-medium"
          : "bg-background-100 text-foreground-700 hover:bg-secondary-200"
      }`}
    >
      {label}
    </button>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="pb-5 mb-5 border-b border-background-200/70 last:border-0 last:mb-0 last:pb-0">
      <h3 className="text-sm font-heading font-bold text-foreground-950 mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({ type, value, onChange }: FilterPanelProps) {
  const years = Array.from(new Set(works.filter((w) => w.type === type).map((w) => w.year))).sort(
    (a, b) => b - a,
  );
  const genreOptions = type === "movie" ? movieGenres : tvGenres;

  const toggleArray = <T,>(arr: T[], item: T): T[] =>
    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

  return (
    <div className="bg-background-50 rounded-lg border border-background-200/80 p-5">
      <Group title="地区">
        {regions.map((r) => (
          <Chip
            key={r}
            label={r}
            active={value.regions.includes(r)}
            onClick={() => onChange({ ...value, regions: toggleArray(value.regions, r) })}
          />
        ))}
      </Group>

      <Group title="年份">
        {years.map((y) => (
          <Chip
            key={y}
            label={`${y}`}
            active={value.years.includes(y)}
            onClick={() => onChange({ ...value, years: toggleArray(value.years, y) })}
          />
        ))}
      </Group>

      <Group title="类型">
        {genreOptions.map((g) => (
          <Chip
            key={g}
            label={g}
            active={value.genres.includes(g)}
            onClick={() => onChange({ ...value, genres: toggleArray(value.genres, g) })}
          />
        ))}
      </Group>

      <Group title="评分">
        {[
          { label: "全部", val: 0 },
          { label: "8 分以上", val: 8 },
          { label: "7 分以上", val: 7 },
          { label: "6 分以上", val: 6 },
        ].map((r) => (
          <Chip
            key={r.label}
            label={r.label}
            active={value.minRating === r.val}
            onClick={() => onChange({ ...value, minRating: r.val })}
          />
        ))}
      </Group>
    </div>
  );
}